"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getBoundingBoxContainerStyle = exports.getImageTransformationsStyle = void 0;
var lodash_1 = __importDefault(require("lodash"));
function getFittedImageScale(containerSize, imageSize) {
    return Math.min(1, containerSize.width / imageSize.width, containerSize.height / imageSize.height);
}
function getTranslateAndScale(containerSize, imageSize, rotation, cropping) {
    var width = imageSize.width, height = imageSize.height;
    if ([90, -90, 270, -270].indexOf(rotation) !== -1) {
        width = imageSize.height;
        height = imageSize.width;
    }
    if (!cropping) {
        // eslint-disable-next-line no-param-reassign
        cropping = {
            x1: 0,
            y1: 0,
            x2: width,
            y2: height,
        };
    }
    // get scale
    var oldScale = getFittedImageScale(containerSize, imageSize);
    var newScale = getFittedImageScale(containerSize, {
        width: cropping.x2 - cropping.x1,
        height: cropping.y2 - cropping.y1,
    });
    var scale = newScale / oldScale;
    // get translate
    var translateX = (((cropping.x1 + cropping.x2 - width) / 2) * scale + imageSize.width / 2) / imageSize.width;
    var translateY = (((cropping.y1 + cropping.y2 - height) / 2) * scale + imageSize.height / 2) / imageSize.height;
    return {
        scale: scale,
        translateX: translateX,
        translateY: translateY,
    };
}
function getInsetPercentage(cropping, width, height) {
    if (cropping) {
        return {
            top: cropping.y1 / height,
            right: (width - cropping.x2) / width,
            bottom: (height - cropping.y2) / height,
            left: cropping.x1 / width,
        };
    }
}
function rotateData(inset, rotation) {
    var data = [inset.top, inset.right, inset.bottom, inset.left];
    var rotateNTimes = rotation > 0 ? rotation / 90 : (rotation + 360) / 90;
    lodash_1.default.times(rotateNTimes, function () {
        var shift = data.shift();
        shift !== undefined && data.push(shift);
    });
    return { top: data[0], right: data[1], bottom: data[2], left: data[3] };
}
function getInset(rotation, imageSize, cropping) {
    if (!cropping || !imageSize) {
        return null;
    }
    var width = imageSize.width, height = imageSize.height;
    if ([90, -90, 270, -270].indexOf(rotation) !== -1) {
        width = imageSize.height;
        height = imageSize.width;
    }
    var insetPercentage = getInsetPercentage(cropping, width, height);
    if (insetPercentage) {
        return rotateData(insetPercentage, rotation);
    }
    return null;
}
function getImageTransformationsStyle(transformations, containerSize, imageSize) {
    // if containerSize is set, it means it's place where might display transformations,
    // in this case, max-width and max-height will be none, and width/height will be manually calculated.
    if (!containerSize || !imageSize || !lodash_1.default.get(imageSize, 'width') || !lodash_1.default.get(imageSize, 'height')) {
        return;
    }
    var style = { transform: '', clipPath: '' };
    var rotation = lodash_1.default.get(transformations, 'rotation');
    var cropping = lodash_1.default.get(transformations, 'cropping');
    // add rotation
    if (rotation) {
        style.transform = "rotate(".concat(rotation, "deg)");
    }
    // add inset
    var inset = getInset(rotation, imageSize, cropping);
    if (inset) {
        style.clipPath = "inset(".concat(inset.top * 100, "% ").concat(inset.right * 100, "% ").concat(inset.bottom * 100, "% ").concat(inset.left * 100, "%)");
    }
    var _a = getTranslateAndScale(containerSize, imageSize, rotation, cropping), scale = _a.scale, translateX = _a.translateX, translateY = _a.translateY;
    // add scale
    style.transform = "scale(".concat(scale, ") ").concat(style.transform);
    // add translate (to center image after cropping and rotation)
    // style.transform = `translate(${-translateX * 100 + 50}%, ${-translateY * 100 + 50}%) ${style.transform}`;
    style.transform = "translate(".concat(-translateX * 100, "%, ").concat(-translateY * 100, "%) ").concat(style.transform);
    return style;
}
exports.getImageTransformationsStyle = getImageTransformationsStyle;
function getBoundingBoxContainerStyle(transforms, containerSize, imageOriginalSize) {
    // if containerSize is set, it means it's place where might display transformations,
    // in this case, max-width and max-height will be none, and width/height will be manually calculated.
    if (!containerSize || !imageOriginalSize || !lodash_1.default.get(imageOriginalSize, 'width') || !lodash_1.default.get(imageOriginalSize, 'height')) {
        return null;
    }
    var croppingInOriginalSize = lodash_1.default.get(transforms, 'cropping');
    var rotation = lodash_1.default.get(transforms, 'rotation');
    var getTranslateAndScale = function (containerSize, naturalImageSize, rotation, cropping) {
        // cropping has coordinates in the new coordinates system after rotation
        var rotatedNaturalImageSize = lodash_1.default.includes([90, -90, 270, -270], rotation)
            ? { width: naturalImageSize.height, height: naturalImageSize.width }
            : naturalImageSize;
        // eslint-disable-next-line no-param-reassign
        cropping = cropping || { x1: 0, y1: 0, x2: rotatedNaturalImageSize.width, y2: rotatedNaturalImageSize.height };
        var cropWidth = cropping.x2 - cropping.x1;
        var cropHeight = cropping.y2 - cropping.y1;
        var scale = getFittedImageScale(containerSize, { width: cropWidth, height: cropHeight });
        // get translate
        var translateX = -0.5 * rotatedNaturalImageSize.width * scale +
            (0.5 * rotatedNaturalImageSize.width - (cropping.x1 + 0.5 * cropWidth)) * scale;
        var translateY = -0.5 * rotatedNaturalImageSize.height * scale +
            (0.5 * rotatedNaturalImageSize.height - (cropping.y1 + 0.5 * cropHeight)) * scale;
        return {
            scale: scale,
            translateX: translateX,
            translateY: translateY,
            width: naturalImageSize.width * scale,
            height: naturalImageSize.height * scale,
        };
    };
    var _a = getTranslateAndScale(containerSize, imageOriginalSize, rotation, croppingInOriginalSize), scale = _a.scale, translateX = _a.translateX, translateY = _a.translateY, width = _a.width, height = _a.height;
    var style = { transform: '', width: '', height: '' };
    var sizeAfterScaleAndRotation = lodash_1.default.includes([90, -90, 270, -270], rotation)
        ? { width: height, height: width }
        : { width: width, height: height };
    var getInset = function (sizeAfterScaleAndRotation, scale, cropping) {
        if (!cropping) {
            return null;
        }
        return {
            top: cropping.y1 * scale, // minus the tag height
            right: sizeAfterScaleAndRotation.width - cropping.x2 * scale,
            bottom: sizeAfterScaleAndRotation.height - cropping.y2 * scale,
            left: cropping.x1 * scale,
        };
    };
    var inset = getInset(sizeAfterScaleAndRotation, scale, croppingInOriginalSize);
    var insetStyle = inset
        ? {
            width: "".concat(sizeAfterScaleAndRotation.width, "px"),
            height: "".concat(sizeAfterScaleAndRotation.height, "px"),
            clipPath: "inset(".concat(inset.top, "px ").concat(inset.right, "px ").concat(inset.bottom, "px ").concat(inset.left, "px)"),
        }
        : null;
    // add translate (to center image after cropping and rotation)
    // style.transform = `translate(${translateX + 50}px, ${translateY + 50}px) ${style.transform}`;
    style.transform = "translate(".concat(translateX, "px, ").concat(translateY, "px) ").concat(style.transform);
    style.width = "".concat(sizeAfterScaleAndRotation.width, "px");
    style.height = "".concat(sizeAfterScaleAndRotation.height, "px");
    return {
        inset: inset,
        style: style,
        scale: scale,
        insetStyle: insetStyle,
        rotation: rotation,
        size: {
            width: sizeAfterScaleAndRotation.width,
            height: sizeAfterScaleAndRotation.height,
        },
    };
}
exports.getBoundingBoxContainerStyle = getBoundingBoxContainerStyle;
